/**
 * @file useCustomerCard.ts
 * @description Данные карточки клиента (phase08.5; phase19.6; phase19.31 —
 * вынесено из страницы): клиент, сводка ценности (`stats`) и необратимое
 * действие «Анонимизировать».
 *
 * Сводка — по возможности: её сбой карточку не ломает (строка сведений
 * просто показывает прочерки). Перечитывание не прячет карточку — прежние
 * данные остаются на экране, пока идёт запрос.
 *
 * Переход к другому клиенту (смена `id`) сбрасывает прежнего сразу, до
 * ответа: иначе «Анонимизировать» во время загрузки сработало бы по прежнему
 * клиенту. «Анонимизировать» (ФЗ-152 ст. 21, необратимо) спрашивает окном
 * кабинета (`useConfirm` + `ConfirmDialog` на странице) в опасном тоне, а не
 * системным `confirm()`.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useNotify } from "../../ui/NotifyProvider";
import { useConfirm, type ConfirmApi, type ConfirmRequest } from "../../ui/use-confirm";
import { ApiError } from "../../../lib/api-client";
import { customers, type CustomerDto, type CustomerStatsDto } from "../../../lib/api-customers";

export interface CustomerCardState {
  customer: CustomerDto | null;
  stats: CustomerStatsDto | null;
  loading: boolean;
  error: string | null;
  anonymizing: boolean;
  refresh: () => void;
  setCustomer: (customer: CustomerDto) => void;
  anonymize: () => void;
  /** Окно подтверждения: страница рисует `ConfirmDialog` по `request`. */
  confirm: Pick<ConfirmApi, "request" | "settle">;
}

/** Вопрос перед анонимизацией — текст прежний (ФЗ-152 ст. 21, операция необратима). */
export const ANONYMIZE_CONFIRM: ConfirmRequest = {
  title: "Анонимизировать клиента?",
  description: "Имя, почта и телефон будут стёрты. Заказы останутся. Действие необратимо.",
  action: "Анонимизировать",
  danger: true,
};

export function useCustomerCard(id: string | undefined): CustomerCardState {
  const notify = useNotify();
  const dialog = useConfirm();
  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  const [stats, setStats] = useState<CustomerStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anonymizing, setAnonymizing] = useState(false);
  const [attempt, setAttempt] = useState(0);
  /** Клиент страницы сейчас — ответы, пришедшие после перехода, не применяем. */
  const idRef = useRef(id);
  idRef.current = id;

  // Другой клиент — прежнего убираем сразу (перечитывание того же — тихое).
  useEffect(() => {
    setCustomer(null);
    setStats(null);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([customers.findOne(id), customers.stats(id).catch(() => null)])
      .then(([c, s]) => {
        if (cancelled) return;
        setCustomer(c);
        setStats(s);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Не удалось загрузить клиента. Попробуйте ещё раз");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, attempt]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);

  const { confirm } = dialog;
  const anonymize = useCallback(() => {
    if (!customer) return;
    const target = customer;
    const startedFor = idRef.current;
    void (async () => {
      if (!(await confirm(ANONYMIZE_CONFIRM))) return;
      // Пока окно было открыто, открыли другого клиента — не трогаем ни того, ни другого.
      if (idRef.current !== startedFor) return;
      setAnonymizing(true);
      try {
        const updated = await customers.anonymize(target.id);
        if (idRef.current === startedFor) setCustomer(updated);
        notify.success("Клиент анонимизирован");
      } catch (err) {
        notify.error(err instanceof ApiError ? err.message : "Не удалось анонимизировать клиента");
      } finally {
        setAnonymizing(false);
      }
    })();
  }, [confirm, customer, notify]);

  return {
    customer,
    stats,
    loading,
    error,
    anonymizing,
    refresh,
    setCustomer,
    anonymize,
    confirm: { request: dialog.request, settle: dialog.settle },
  };
}
